import { useCallback, useEffect, useState } from "react";
import { useAppContext } from "../../../providers/ProviderAppContainer";
import Loading from "../../common/Loading";
import NoContent from "../../common/NoContent";
import TabHeader from "../../common/TabHeader";
import { Button } from "primereact/button";
import OrderManager from "../../common/OrderManager";
import HasRequiredAuthority from "../../dependencies/HasRequiredAuthority";
import { AUTHORITIES } from "../../../constants";
import DashboardPromoDialogContent from "./DashboardPromoDialogContent";
import DialogAddDashboardPromoDialogContent from "./DialogAddDashboardPromoDialogContent";
import DialogEditDashboardPromoDialogContent from "./DialogEditDashboardPromoDialogContent";

export default function DashboardPromoDialogsTab() {
    const { requestAPI, showToast } = useAppContext();

    const [contents, setContents] = useState([]);
    const [loading, setLoading] = useState();
    const [error, setError] = useState();
    const [updatingViewIndex, setUpdatingViewIndex] = useState();
    const [savingViewIndex, setSavingViewIndex] = useState();
    const [dialogAddDashboardPromoDialogContent, setDialogAddDashboardPromoDialogContent] = useState({ visible: false });
    const [dialogEditDashboardPromoDialogContent, setDialogEditDashboardPromoDialogContent] = useState({ visible: false });

    const closeDialogAddDashboardPromoDialogContent = useCallback(() => {
        setDialogAddDashboardPromoDialogContent((prev) => ({ ...prev, visible: false }));
    }, []);

    const closeDialogEditDashboardPromoDialogContent = useCallback(() => {
        setDialogEditDashboardPromoDialogContent((prev) => ({ ...prev, visible: false }));
    }, []);

    useEffect(() => {
        requestAPI({
            requestPath: "dialog-contents/dashboard/contents",
            requestMethod: "GET",
            onRequestStart: () => setError(),
            setLoading: setLoading,
            onRequestFailure: setError,
            onResponseReceieved: (contentsResponse, responseCode) => {
                if (Array.isArray(contentsResponse) && responseCode === 200) {
                    setContents(contentsResponse);
                } else {
                    setError("Couldn't load dashboard promo dialogs");
                }
            },
        });
    }, [requestAPI]);

    const updateViewIndexes = useCallback(() => {
        requestAPI({
            requestPath: "dialog-contents/dashboard/view_indexes",
            requestMethod: "PATCH",
            requestPostBody: contents.map(({ id }, view_index) => ({ id, view_index })),
            setLoading: setSavingViewIndex,
            parseResponseBody: false,
            onRequestFailure: () => showToast({ severity: "error", summary: "Failed", detail: "Failed To Update View Indexes !", life: 2000 }),
            onResponseReceieved: (_, responseCode) => {
                if (responseCode === 200) {
                    setContents((prev) => prev.map((content, view_index) => ({ ...content, view_index })));
                    showToast({ severity: "success", summary: "Updated", detail: "View Indexes Updated", life: 1000 });
                } else {
                    showToast({ severity: "error", summary: "Failed", detail: "Failed To Update View Indexes !", life: 2000 });
                }
            },
        });
    }, [contents, requestAPI, showToast]);

    if (loading && !contents.length && !error) {
        return <Loading message="Loading dashboard promo dialogs" />;
    }

    if (error) {
        return <NoContent error={error} />;
    }

    return (
        <div className="flex flex-column h-full overflow-hidden">
            <TabHeader
                className="px-3 pt-3"
                title="Dashboard Promo Dialogs"
                highlights={["Global promo slides shown on the dashboard"]}
                actionItems={[
                    <HasRequiredAuthority requiredAuthority={AUTHORITIES.UPDATE_COURSE}>
                        <Button
                            icon="pi pi-plus"
                            severity="warning"
                            onClick={() => setDialogAddDashboardPromoDialogContent((prev) => ({ ...prev, visible: true }))}
                        />
                    </HasRequiredAuthority>,
                    !!contents?.length ? (
                        <HasRequiredAuthority requiredAuthority={AUTHORITIES.UPDATE_COURSE}>
                            <Button
                                loading={savingViewIndex}
                                onClick={() => {
                                    showToast({
                                        severity: "info",
                                        summary: "Repositioning",
                                        detail: `Repositioning Mode ${!updatingViewIndex ? "Enabled" : "Disabled"}`,
                                        life: 1000,
                                    });
                                    if (updatingViewIndex) {
                                        updateViewIndexes();
                                    }
                                    setUpdatingViewIndex((prev) => !prev);
                                }}
                                icon="pi pi-arrows-v"
                            />
                        </HasRequiredAuthority>
                    ) : null,
                ].filter(Boolean)}
            />

            <div className="flex-1 min-h-0 overflow-hidden flex flex-column mt-3">
                <OrderManager
                    updatingViewIndex={updatingViewIndex}
                    items={contents}
                    setItems={setContents}
                    entity="Promo Contents"
                    itemTemplate={(item) => (
                        <DashboardPromoDialogContent
                            {...item}
                            updatingViewIndex={updatingViewIndex}
                            setContents={setContents}
                            setDialogEditDashboardPromoDialogContent={setDialogEditDashboardPromoDialogContent}
                        />
                    )}
                />
            </div>

            {dialogAddDashboardPromoDialogContent?.visible && (
                <DialogAddDashboardPromoDialogContent
                    {...dialogAddDashboardPromoDialogContent}
                    closeDialog={closeDialogAddDashboardPromoDialogContent}
                    setContents={setContents}
                />
            )}
            {dialogEditDashboardPromoDialogContent?.visible && (
                <DialogEditDashboardPromoDialogContent
                    closeDialog={closeDialogEditDashboardPromoDialogContent}
                    setContents={setContents}
                    {...dialogEditDashboardPromoDialogContent}
                />
            )}
        </div>
    );
}
